import { useRoleLabels } from "../lib/RoleLabelsContext";
import { Recommendations } from "./Recommendations";
import type { Account, NavigationNode, Role } from "../lib/types";
import { TherapistNotifications } from "./TherapistNotifications";
import { IndividualExerciseHome } from "./IndividualExerciseHome";
import { LearnerHomeOverview } from "./LearnerHomeOverview";

type StartPageAudience = "professional" | "learner";
const LEGACY_START_PAGE_SLUG = "willkommen-in-v2";

interface StartPageProps {
  account: Account;
  navigation: NavigationNode[];
  canUseLearningDashboard: boolean;
  canUseManagement: boolean;
  canUseQualitativeAnalysis: boolean;
  onNavigateToPage: (slug: string) => void;
  onNavigateToRoute: (route: "learning" | "management" | "analysis" | "individual-exercises") => void;
  onOpenLearnerAnalysis: (learnerId: string, runId: string) => Promise<void>;
}

export function startPageAudience(roles: Role[]): StartPageAudience {
  return roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST")
    ? "professional"
    : "learner";
}

export function navigationWithoutLegacyStartPage(nodes: NavigationNode[]): NavigationNode[] {
  return nodes.flatMap((node) => {
    if (node.pageSlug === LEGACY_START_PAGE_SLUG) return [];
    const children = navigationWithoutLegacyStartPage(node.children);
    if (node.type !== "PAGE" && node.children.length > 0 && children.length === 0) return [];
    return [{ ...node, children }];
  });
}

export function firstNavigationPage(nodes: NavigationNode[]): string | null {
  for (const node of nodes) {
    if (node.pageSlug) return node.pageSlug;
    const nested = firstNavigationPage(node.children);
    if (nested) return nested;
  }
  return null;
}

function firstName(displayName: string) {
  return displayName.trim().split(/\s+/)[0] || displayName;
}

function LearnerStartPage({
  account,
  navigation,
  canUseLearningDashboard,
  onNavigateToPage,
  onNavigateToRoute,
}: StartPageProps) {
  const learningWorlds = navigation
    .map((subject) => ({ ...subject, firstPage: firstNavigationPage([subject]) }))
    .filter((subject) => subject.firstPage);

  return (
    <div className="start-page learner-start-page">
      <header className="learner-welcome">
        <div>
          <p className="eyebrow">Dein Platz zum Lernen</p>
          <h1>Hallo {firstName(account.displayName)}!</h1>
          <p>Hier siehst du, was heute ansteht und was du zuletzt geübt hast.</p>
        </div>
        <img src="/brand/lepi-winkend.webp" alt="" />
      </header>

      {canUseLearningDashboard && <LearnerHomeOverview key={account.id} account={account} />}

      <section className="start-section" aria-labelledby="learning-worlds-title">
        <div className="start-section-heading">
          <div>
            <p className="eyebrow">Such dir etwas aus</p>
            <h2 id="learning-worlds-title">Weitere Übungen entdecken</h2>
          </div>
        </div>
        {learningWorlds.length > 0 ? (
          <div className="learning-world-grid">
            {learningWorlds.map((subject, index) => (
              <button
                className={`learning-world-card world-${(index % 4) + 1}`}
                key={subject.id}
                onClick={() => { if (subject.type === "PAGE" && subject.firstPage) onNavigateToPage(subject.firstPage); else window.location.hash = `#/subject/${encodeURIComponent(subject.id)}`; }}
              >
                <span className="learning-world-icon" aria-hidden="true">{subject.icon || "✏️"}</span>
                <span>
                  <strong>{subject.title}</strong>
                  <small>{subject.description || "Lernen, üben und sicherer werden"}</small>
                </span>
                <b aria-hidden="true">→</b>
              </button>
            ))}
          </div>
        ) : (
          <div className="start-empty-state">Sobald Lerninhalte bereitstehen, findest du sie hier und links im Menü.</div>
        )}
        <div className="learner-more-actions">
          {account.modules.flashcards && <a className="learner-section-link" href="#/flashcards">Karteikarten öffnen <span aria-hidden="true">→</span></a>}
          {canUseLearningDashboard && <button className="secondary" onClick={() => onNavigateToRoute("learning")}>Mein Lernbereich</button>}
        </div>
      </section>

      <p className="learner-home-encouragement">Jeder Versuch zählt. Nimm dir Zeit – Schritt für Schritt.</p>
    </div>
  );
}

function ProfessionalStartPage({
  account,
  canUseLearningDashboard,
  canUseManagement,
  canUseQualitativeAnalysis,
  onNavigateToRoute,
  onOpenLearnerAnalysis,
}: StartPageProps) {
  const labels = useRoleLabels();
  return (
    <div className="start-page professional-start-page">
      <section className="start-hero professional-start-hero">
        <div className="start-hero-copy">
          <p className="eyebrow">Ihr Ausgangspunkt für die Lernbegleitung</p>
          <h1><span>Willkommen zurück,</span><strong>{account.displayName}.</strong></h1>
          <p>Von hier aus gelangen Sie direkt zu den Bereichen, die Sie für die Lernbegleitung brauchen.</p>
          <div className="start-actions">
            {canUseManagement && (
              <button className="primary" onClick={() => onNavigateToRoute("management")}>{labels.LEARNER.plural} verwalten <span aria-hidden="true">→</span></button>
            )}
          </div>
        </div>
        <div className="start-hero-visual" aria-hidden="true">
          <span className="start-note-card note-card-one">klar</span>
          <span className="start-note-card note-card-two">begleitend</span>
          <img src="/brand/lepi-stift.webp" alt="" />
        </div>
      </section>

      <section className="start-section" aria-labelledby="quick-access-title">
        <div className="start-section-heading">
          <div>
            <p className="eyebrow">Direkt weiterarbeiten</p>
            <h2 id="quick-access-title">Schnellzugriff</h2>
          </div>
          <span>Die wichtigsten Werkzeuge an einem Ort.</span>
        </div>
        <div className="professional-action-grid">
          {canUseManagement && (
            <button className="professional-action-card management-action" onClick={() => onNavigateToRoute("management")}>
              <span className="professional-action-icon" aria-hidden="true">↗</span>
              <span><strong>Verwaltung</strong><small>Konten und Zuordnungen organisieren ({labels.LEARNER.plural})</small></span>
              <b aria-hidden="true">→</b>
            </button>
          )}
          {canUseQualitativeAnalysis && (
            <button className="professional-action-card analysis-action" onClick={() => onNavigateToRoute("analysis")}>
              <span className="professional-action-icon" aria-hidden="true">◎</span>
              <span><strong>Qualitative Analyse</strong><small>Lernverläufe und fachliche Hinweise einordnen</small></span>
              <b aria-hidden="true">→</b>
            </button>
          )}
          {!account.roles.includes("ADMIN") && canUseLearningDashboard && (
            <button className="professional-action-card learning-action" onClick={() => onNavigateToRoute("learning")}>
              <span className="professional-action-icon" aria-hidden="true">✓</span>
              <span><strong>{account.actingAs ? `Lernbereich von ${account.actingAs.displayName}` : "Lernbereich"}</strong><small>Empfehlungen, Merkliste und Übungsverlauf öffnen</small></span>
              <b aria-hidden="true">→</b>
            </button>
          )}
        </div>
      </section>

      {!account.roles.includes("ADMIN") && account.actingAs && <Recommendations key={account.actingAs.id} account={account} home />}

      <IndividualExerciseHome account={account} />

      {account.roles.includes("THERAPIST") && <TherapistNotifications onOpenLearnerAnalysis={onOpenLearnerAnalysis} />}

      <section className="professional-workflow">
        <div className="workflow-copy">
          <p className="eyebrow">Ein klarer Ablauf</p>
          <h2>Lernbegleitung in drei Schritten</h2>
          <p>Wählen Sie ein Konto mit der Rolle „{labels.LEARNER.singular}“, stellen Sie passende Übungen zusammen und nutzen Sie den Verlauf für die weitere Planung.</p>
        </div>
        <ol className="workflow-steps">
          <li><span>1</span><div><strong>{labels.LEARNER.plural} auswählen</strong><small>Über die Verwaltung oder „Bearbeiten als“.</small></div></li>
          <li><span>2</span><div><strong>Übungen empfehlen</strong><small>Passende Inhalte gezielt bereitstellen.</small></div></li>
          <li><span>3</span><div><strong>Entwicklung begleiten</strong><small>Ergebnisse fachlich und ohne Druck einordnen.</small></div></li>
        </ol>
        <img src="/brand/lepi-erklaerend.webp" alt="" />
      </section>
    </div>
  );
}

export function StartPage(props: StartPageProps) {
  return startPageAudience(props.account.roles) === "professional"
    ? <ProfessionalStartPage {...props} />
    : <LearnerStartPage {...props} />;
}
